import React from 'react';
import { Document, Page, View, Text, StyleSheet, Image } from '@react-pdf/renderer';
import type { ChecklistAnswer, ChecklistItemDef } from './vehicleChecklistItems';

const styles = StyleSheet.create({
  page: { padding: 32, fontSize: 9, fontFamily: 'Helvetica', color: '#111827' },
  headerRow: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12, gap: 12 },
  logo: { width: 130, height: 34, objectFit: 'contain' },
  title: { fontSize: 14, fontWeight: 700 },
  subtitle: { marginTop: 3, color: '#6B7280', fontSize: 8 },
  box: { borderWidth: 1, borderColor: '#E5E7EB', borderRadius: 6, padding: 10, marginBottom: 12 },
  boxRow: { flexDirection: 'row', marginBottom: 3 },
  boxLabel: { width: 92, color: '#6B7280', fontSize: 8 },
  boxValue: { flexGrow: 1, fontSize: 9, fontWeight: 600 },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    marginTop: 8,
    marginBottom: 6,
    paddingBottom: 3,
    borderBottomWidth: 1,
    borderBottomColor: '#E5E7EB',
    color: '#1F2937',
  },
  row: { flexDirection: 'row', borderBottomWidth: 0.5, borderBottomColor: '#F3F4F6', paddingVertical: 5, alignItems: 'flex-start' },
  colItem: { flexGrow: 1, flexShrink: 1, paddingRight: 6, fontSize: 8 },
  colMark: { width: 22, textAlign: 'center', fontSize: 7, color: '#374151' },
  foot: { marginTop: 12, paddingTop: 8, borderTopWidth: 1, borderTopColor: '#E5E7EB', fontSize: 8, color: '#4B5563' },
  disclaimer: { marginTop: 8, fontSize: 7, color: '#9CA3AF', lineHeight: 1.35 },
  photoPageTitle: { fontSize: 11, fontWeight: 700, marginBottom: 10, color: '#1F2937' },
  photoFrame: {
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 6,
    padding: 8,
    marginBottom: 10,
    alignItems: 'center',
    justifyContent: 'center',
    /* Altura fixa ajuda o layout do Yoga/React-PDF; sem isso Image às vezes sai com área zero. */
    height: 360,
    width: '100%',
  },
  photoImg: { width: '100%', height: 340, objectFit: 'contain' },
  photoDesc: {
    marginTop: 10,
    fontSize: 9,
    color: '#374151',
    lineHeight: 1.45,
    width: '100%',
  },
  photoDescMuted: { marginTop: 10, fontSize: 8, color: '#9CA3AF', fontStyle: 'italic' },
  photoHint: { fontSize: 8, color: '#6B7280', marginTop: 8, marginBottom: 4 },
});

export interface VehicleChecklistContext {
  brand: string;
  model: string;
  year: string;
  color: string;
  plateOrId: string;
  km: string;
  chassis: string;
  isConsignado: boolean;
  consignmentOwner?: string;
  consignmentContact?: string;
  vehicleIdLabel?: string;
}

export interface ChecklistPhotoEntry {
  /** data URI ou URL válida para @react-pdf Image */
  src: string;
  description?: string;
}

export interface VehicleChecklistPDFProps {
  meta: VehicleChecklistContext;
  items: ChecklistItemDef[];
  answers: Record<string, ChecklistAnswer>;
  generatedAtLabel: string;
  inspectorName?: string;
  inspectorRole?: string;
  location?: string;
  generalNotes?: string;
  logoUrl?: string | null;
  photos?: ChecklistPhotoEntry[];
}

const Disclaimer: React.FC = () => (
  <Text style={styles.disclaimer}>
    Este documento é gerado no sistema para apoio à operação da loja. Não substitui laudo técnico formal.
  </Text>
);

const VehicleChecklistPDF: React.FC<VehicleChecklistPDFProps> = ({
  meta,
  items,
  answers,
  generatedAtLabel,
  inspectorName,
  inspectorRole,
  location,
  generalNotes,
  logoUrl,
  photos = [],
}) => {
  console.log('[VehicleChecklistPDF] render do Document', {
    quantidadeFotos: photos.length,
    primeiraSrc:
      photos[0]?.src != null && typeof photos[0].src === 'string'
        ? `${photos[0].src.slice(0, 40)}… (${photos[0].src.length} chars)`
        : photos[0]?.src,
  });

  const sections = Array.from(new Set(items.map((i) => i.section)));
  const hasPhotos = photos.length > 0;

  const checklistBlock = (
    <>
      <View style={styles.headerRow}>
        {logoUrl ? <Image src={logoUrl} style={styles.logo} /> : <View style={{ width: 130 }} />}
        <View style={{ flexGrow: 1 }}>
          <Text style={styles.title}>Checklist de vistoria / entrada de veículo</Text>
          <Text style={styles.subtitle}>Gerado em {generatedAtLabel}</Text>
          {meta.isConsignado && (
            <Text style={{ ...styles.subtitle, color: '#B91C1C', fontWeight: 700 }}>
              Veículo em consignação — conferência reforçada recomendada
            </Text>
          )}
        </View>
      </View>

      <View style={styles.box}>
        <View style={styles.boxRow}>
          <Text style={styles.boxLabel}>Veículo</Text>
          <Text style={styles.boxValue}>
            {meta.brand} {meta.model} {meta.year ? `(${meta.year})` : ''}
          </Text>
        </View>
        <View style={styles.boxRow}>
          <Text style={styles.boxLabel}>Cor / ID</Text>
          <Text style={styles.boxValue}>
            {meta.color || '—'} · {meta.plateOrId || '—'}
          </Text>
        </View>
        <View style={styles.boxRow}>
          <Text style={styles.boxLabel}>Quilom. / horas</Text>
          <Text style={styles.boxValue}>{meta.km || '—'}</Text>
        </View>
        <View style={styles.boxRow}>
          <Text style={styles.boxLabel}>Chassi</Text>
          <Text style={styles.boxValue}>{meta.chassis || '—'}</Text>
        </View>
        {meta.vehicleIdLabel && (
          <View style={styles.boxRow}>
            <Text style={styles.boxLabel}>Cadastro sistema</Text>
            <Text style={styles.boxValue}>ID {meta.vehicleIdLabel}</Text>
          </View>
        )}
        {meta.isConsignado && (meta.consignmentOwner || meta.consignmentContact) && (
          <View style={styles.boxRow}>
            <Text style={styles.boxLabel}>Consignante</Text>
            <Text style={styles.boxValue}>
              {[meta.consignmentOwner, meta.consignmentContact].filter(Boolean).join(' · ') || '—'}
            </Text>
          </View>
        )}
      </View>

      {(inspectorName || location) && (
        <View style={styles.box}>
          {inspectorName ? (
            <View style={styles.boxRow}>
              <Text style={styles.boxLabel}>Responsável</Text>
              <Text style={styles.boxValue}>
                {inspectorName}
                {inspectorRole ? ` — ${inspectorRole}` : ''}
              </Text>
            </View>
          ) : null}
          {location ? (
            <View style={styles.boxRow}>
              <Text style={styles.boxLabel}>Local</Text>
              <Text style={styles.boxValue}>{location}</Text>
            </View>
          ) : null}
        </View>
      )}

      <Text style={{ fontSize: 7, color: '#6B7280', marginBottom: 4 }}>
        Legenda: &quot;X&quot; na coluna do status verificado; célula vazia = não marcado no formulário.
      </Text>

      {sections.map((section) => {
        const rows = items.filter((i) => i.section === section);
        if (!rows.length) return null;
        return (
          <View key={section}>
            <Text style={styles.sectionTitle}>{section}</Text>
            <View
              style={{ flexDirection: 'row', borderBottomWidth: 1, borderBottomColor: '#E5E7EB', paddingBottom: 4, marginBottom: 2 }}
            >
              <Text style={[styles.colItem, { fontWeight: 700 }]}>Item</Text>
              <Text style={[styles.colMark, { fontWeight: 700 }]}>Conf.</Text>
              <Text style={[styles.colMark, { fontWeight: 700 }]}>Não conf.</Text>
              <Text style={[styles.colMark, { fontWeight: 700 }]}>N/A</Text>
            </View>
            {rows.map((it) => {
              const a = answers[it.id] || '';
              return (
                <View key={it.id} style={styles.row}>
                  <Text style={styles.colItem}>{it.label}</Text>
                  <Text style={styles.colMark}>{a === 'ok' ? 'X' : ''}</Text>
                  <Text style={styles.colMark}>{a === 'nc' ? 'X' : ''}</Text>
                  <Text style={styles.colMark}>{a === 'na' ? 'X' : ''}</Text>
                </View>
              );
            })}
          </View>
        );
      })}

      {generalNotes?.trim() ? (
        <View style={styles.foot}>
          <Text style={{ fontWeight: 700, marginBottom: 4 }}>Observações gerais</Text>
          <Text style={{ lineHeight: 1.4 }}>{generalNotes.trim()}</Text>
        </View>
      ) : null}

      {hasPhotos ? (
        <Text style={styles.photoHint}>
          Registro fotográfico: {photos.length} {photos.length === 1 ? 'imagem nas páginas seguintes.' : 'imagens nas páginas seguintes.'}
        </Text>
      ) : (
        <Disclaimer />
      )}
    </>
  );

  return (
    <Document title="Checklist de vistoria">
      <Page size="A4" style={styles.page}>
        {checklistBlock}
      </Page>

      {photos.map((ph, i) => (
        <Page key={`photo-${i}`} size="A4" style={styles.page}>
          <Text style={styles.photoPageTitle}>
            Registro fotográfico — foto {i + 1} de {photos.length}
          </Text>
          <View style={styles.photoFrame}>
            <Image src={ph.src} style={styles.photoImg} />
          </View>
          {ph.description?.trim() ? (
            <Text style={styles.photoDesc}>{ph.description.trim()}</Text>
          ) : (
            <Text style={styles.photoDescMuted}>Sem descrição informada.</Text>
          )}
          {i === photos.length - 1 ? (
            <View style={{ marginTop: 16 }}>
              <Disclaimer />
            </View>
          ) : null}
        </Page>
      ))}
    </Document>
  );
};

export default VehicleChecklistPDF;
